'use client';

import { useState, useEffect } from 'react';

interface GameOverModalProps {
  isVisible: boolean;
  score: number;
  hasWon: boolean;
  onRestart: () => void;
  onContinue: () => void;
  onClose: () => void;
}

export default function GameOverModal({
  isVisible,
  score,
  hasWon,
  onRestart,
  onContinue,
  onClose
}: GameOverModalProps) {
  const [isShown, setIsShown] = useState(false);
  
  // Handle visibility change with animation effect
  useEffect(() => {
    if (isVisible) {
      setIsShown(true);
    } else {
      const timer = setTimeout(() => {
        setIsShown(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);
  
  if (!isShown) return null;

  // Determine modal content based on game outcome
  const modalTitle = hasWon ? 'You Win!' : 'Game Over!';
  const modalMessage = hasWon 
    ? 'Congratulations! You reached 2048!' 
    : 'No more moves available.';

  return (
    <div 
      className={`fixed inset-0 flex items-center justify-center bg-black/50 z-[999] transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl p-6 w-80 text-center transform transition-transform duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={`text-2xl font-bold mb-2 ${hasWon ? 'text-yellow-500' : 'text-red-500'}`}>
          {modalTitle}
        </h2>
        
        <p className="text-gray-700 mb-4">{modalMessage}</p>
        
        <div className="text-xl font-bold mb-6">
          Score: {score}
        </div>
        
        <div className="flex justify-center gap-3">
          {hasWon && (
            <button
              onClick={onContinue}
              className="btn bg-[#8f7a66] hover:bg-[#7c6957] text-white border-none"
            >
              Keep Going
            </button>
          )}
          
          <button
            onClick={onRestart}
            className="btn bg-[#8f7a66] hover:bg-[#7c6957] text-white border-none"
          >
            New Game
          </button>
          
          <button
            onClick={onClose}
            className="btn bg-gray-500 hover:bg-gray-600 text-white border-none"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}